import { useCallback, useEffect, useRef, useState } from 'react';
import { RequiredKeysOf } from 'type-fest';

import { Pagination } from '@typings';
import { noop } from '@utils';

type FetcherFunction<T, P extends Pagination.BaseParams> = (
  params: P,
  signal?: AbortSignal,
) => Promise<Pagination.Response<T>>;

type AdditionalRequiredKeys<P> = RequiredKeysOf<Omit<P, 'page' | 'size'>>;

type AdditionalRequiredParams<P> = AdditionalRequiredKeys<P> extends never
  ? { params?: Omit<P, 'page' | 'size'> }
  : { params: Omit<P, 'page' | 'size'> };

type Options = {
  /** Enables or disables the hook entirely (default: true) */
  enabled?: boolean;

  /** Enables infinite pagination (accumulating data) vs replacing data (default: true) */
  infinite?: boolean;

  /** Whether to run the first fetch immediately on mount (default: true) */
  immediate?: boolean;

  /** Starting page number (default: 1) */
  initialPage?: number;

  /** Number of items per page (default: 50) */
  initialSize?: number;

  /** Enables caching of fetched pages (default: true) */
  enableCache?: boolean;
};

type UsePaginationFetcherReturn<T> = {
  /** Fetched data items */
  data: T[];

  /** Whether data is currently being loaded */
  isLoading: boolean;

  /** Any error thrown during the fetch */
  error: Error | null;

  /** Current page number */
  page: number;

  /** Number of items per page */
  size: number;

  /** Total number of items across all pages */
  total: number;

  /** Total number of pages */
  pages: number;

  /** Whether there is a next page available */
  hasNext: boolean;

  /** Whether there is a previous page available */
  hasPrev: boolean;

  /** Manually set the current page */
  setPage: (page: number) => void;

  /** Manually set the page size */
  setSize: (size: number) => void;

  /** Move to the next page (if available) */
  onNext: () => void;

  /** Move to the previous page (if available) */
  onPrev: () => void;

  /** Reset pagination to the first page */
  resetPagination: () => void;

  /** Refetch the current page manually */
  refetch: () => void;

  /** Clears the internal response cache */
  clearCache: () => void;
};

type UsePaginationFetcherParams<T, P extends Pagination.BaseParams> =
  | {
      /** Async fetcher function that returns paginated data */
      fetcher: FetcherFunction<T, P>;

      /** Options with required `enabled` */
      options: Options & { enabled: boolean };

      /** Additional request parameters (optional) */
      params?: Partial<Omit<P, 'page' | 'size'>>;
    }
  | ({
      fetcher: FetcherFunction<T, P>;

      /** Optional options object (without `enabled`) */
      options?: Exclude<Options, 'enabled'>;
    } & AdditionalRequiredParams<P>);

export function usePaginationFetcher<
  T,
  P extends Pagination.BaseParams = Pagination.BaseParams,
>({
  fetcher,
  options,
  params,
}: UsePaginationFetcherParams<T, P>): UsePaginationFetcherReturn<T> {
  const {
    infinite = true,
    immediate = true,
    initialPage = 1,
    initialSize = 50,
    enabled = true,
    enableCache = true,
  } = options ?? {};

  const [data, setData] = useState<T[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [pages, setPages] = useState<number>(0);
  const [page, setPage] = useState<number>(initialPage);
  const [size, setSize] = useState<number>(initialSize);

  const [isLoading, setIsLoading] = useState<boolean>(immediate);
  const [error, setError] = useState<Error | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const cacheRef = useRef<Map<string, Pagination.Response<T>>>(new Map());

  const resetPagination = () => setPage(initialPage);

  useEffect(() => {
    setData([]);
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const getCacheKey = useCallback(
    (page: number, size: number) => `${page}-${size}-${JSON.stringify(params)}`,
    [params],
  );

  const fetchData = useCallback(async () => {
    const cacheKey = getCacheKey(page, size);

    if (enableCache && cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey)!;
      setData(cached.items);
      setTotal(cached.total);
      setPages(cached.pages);
      return;
    }

    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const fetcherParams: P = {
        ...params,
        page,
        size,
      } as P;

      const result = await fetcher(fetcherParams, controller.signal);

      if (enableCache) {
        cacheRef.current.set(cacheKey, result);
      }

      if (infinite) {
        setData((prev) => [...prev, ...result.items]);
      } else {
        setData(result.items);
      }

      setTotal(result.total);
      setPages(result.pages);
    } catch (err) {
      if ((err as any).name !== 'AbortError') {
        setError(err as Error);
      }
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, fetcher, params, infinite]);

  useEffect(() => {
    if (enabled && immediate) {
      fetchData();
    }
    return () => {
      abortRef.current?.abort();
    };
  }, [enabled, fetchData, immediate]);

  const onNext = () => {
    if (!isLoading && page < pages) {
      setPage((prev) => prev + 1);
    }
  };

  const onPrev = () => {
    if (page > 1) {
      setPage((prev) => prev - 1);
    }
  };

  const clearCache = () => {
    cacheRef.current.clear();
  };

  return {
    data,
    page,
    size,
    total,
    pages,
    error,
    isLoading,
    hasNext: page < pages,
    hasPrev: page > 1,
    onNext,
    onPrev,
    setPage,
    setSize,
    clearCache,
    resetPagination,
    refetch: enabled ? fetchData : noop,
  };
}
